import { type MutableRefObject, useEffect } from "react";
import {
  type BrowserCommand,
  type BrowserSessionSnapshot,
  type BrowserTabSnapshot,
  type DevTunnelRecord,
  type EnvironmentApi,
  type EnvironmentId,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { useBrowserStateStore } from "~/browserStateStore";
import {
  browserDisplayUrl,
  buildBrowserPreviewSubscriptionInput,
  mergeDevTunnelRecords,
} from "./BrowserPreviewPanel.logic";
import type { BrowserOptionsAction } from "./BrowserPreviewPanel.sessionControls";

const DEFAULT_BROWSER_HOME_URL = "https://www.google.com/";
const BROWSER_ATTACH_TIMEOUT_MS = 3_000;

export interface BrowserPreviewLifecycleEffectsOptions {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly addressInputFocused: boolean;
  readonly addressInputFocusedRef: MutableRefObject<boolean>;
  readonly browserRuntimeScopeKeyRef: MutableRefObject<string>;
  readonly commandGenerationRef: MutableRefObject<number>;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly environmentApi: EnvironmentApi | undefined;
  readonly environmentId: EnvironmentId;
  readonly historyRestoreAttemptKeyRef: MutableRefObject<string | null>;
  readonly interactiveKeyboardQueueRef: MutableRefObject<Promise<void>>;
  readonly interactivePointerQueueRef: MutableRefObject<Promise<void>>;
  readonly lastInteractivePointerPointRef: MutableRefObject<{
    readonly tabId: string;
    readonly x: number;
    readonly y: number;
  } | null>;
  readonly lastSyncedPreviewTabIdRef: MutableRefObject<string | null>;
  readonly lifecycleAction: BrowserOptionsAction | null;
  readonly openingBrowserSession: boolean;
  readonly passiveSessionAttachKeyRef: MutableRefObject<string | null>;
  readonly pendingBrowserCommand: BrowserCommand["type"] | null;
  readonly pendingBrowserCommandTokenRef: MutableRefObject<string | null>;
  readonly previewLastUrl: string | null | undefined;
  readonly previewRememberLastUrl: boolean;
  readonly recoverableDevPreviewUrl: string | null;
  readonly session: BrowserSessionSnapshot | null;
  readonly sessionOpenInitialUrlRef: MutableRefObject<string>;
  readonly setAddressInputFocused: (focused: boolean) => void;
  readonly setAddressValue: (value: string) => void;
  readonly setBrowserRuntimeError: (message: string | null) => void;
  readonly setConnecting: (connecting: boolean) => void;
  readonly setHasBrowserRuntime: (hasRuntime: boolean) => void;
  readonly setOpeningBrowserSession: (opening: boolean) => void;
  readonly setPassiveAttachSettled: (settled: boolean) => void;
  readonly setPendingBrowserCommand: (command: BrowserCommand["type"] | null) => void;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly setThreadDevPort: (port: number | null) => void;
  readonly setThreadDevPreviewUrl: (url: string | null) => void;
  readonly setTrackedDevTunnelRecords: (
    next:
      | ReadonlyArray<DevTunnelRecord>
      | ((records: ReadonlyArray<DevTunnelRecord>) => ReadonlyArray<DevTunnelRecord>),
  ) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}

export function useBrowserPreviewLifecycleEffects({
  activeTab,
  addressInputFocused,
  addressInputFocusedRef,
  browserRuntimeScopeKeyRef,
  commandGenerationRef,
  devTunnelRecords,
  environmentApi,
  environmentId,
  historyRestoreAttemptKeyRef,
  interactiveKeyboardQueueRef,
  interactivePointerQueueRef,
  lastInteractivePointerPointRef,
  lastSyncedPreviewTabIdRef,
  lifecycleAction,
  openingBrowserSession,
  passiveSessionAttachKeyRef,
  pendingBrowserCommand,
  pendingBrowserCommandTokenRef,
  previewLastUrl,
  previewRememberLastUrl,
  recoverableDevPreviewUrl,
  session,
  sessionOpenInitialUrlRef,
  setAddressInputFocused,
  setAddressValue,
  setBrowserRuntimeError,
  setConnecting,
  setHasBrowserRuntime,
  setOpeningBrowserSession,
  setPassiveAttachSettled,
  setPendingBrowserCommand,
  setSessionSnapshot,
  setThreadDevPort,
  setThreadDevPreviewUrl,
  setTrackedDevTunnelRecords,
  threadId,
  threadRef,
}: BrowserPreviewLifecycleEffectsOptions): void {
  useEffect(() => {
    if (addressInputFocused) {
      return;
    }
    if (!activeTab?.url) {
      setAddressValue(recoverableDevPreviewUrl ?? DEFAULT_BROWSER_HOME_URL);
      return;
    }
    setAddressValue(browserDisplayUrl(activeTab.url, devTunnelRecords) ?? activeTab.url);
  }, [
    activeTab?.url,
    addressInputFocused,
    devTunnelRecords,
    recoverableDevPreviewUrl,
    setAddressValue,
  ]);

  useEffect(() => {
    if (!environmentApi?.threadDevRuntime) {
      return;
    }
    let cancelled = false;
    void environmentApi.threadDevRuntime
      .get({ threadId })
      .then((record) => {
        if (!cancelled) {
          setThreadDevPort(record?.allocatedPort ?? null);
          setThreadDevPreviewUrl(record?.previewUrl ?? null);
        }
      })
      .catch(() => {
        // Best-effort, exactly like the dev-tunnel read below: the panel opens
        // whether or not a dev port has been allocated, and an environment that
        // answers this call with a record the client cannot decode should leave
        // the port unknown rather than raise an unhandled rejection.
      });
    return () => {
      cancelled = true;
    };
  }, [environmentApi, setThreadDevPort, setThreadDevPreviewUrl, threadId]);

  useEffect(() => {
    if (!environmentApi?.devTunnel) {
      setTrackedDevTunnelRecords([]);
      return;
    }
    let cancelled = false;
    setTrackedDevTunnelRecords([]);
    void environmentApi.devTunnel
      .list({ threadId })
      .then((records) => {
        if (!cancelled) {
          setTrackedDevTunnelRecords((currentRecords) =>
            mergeDevTunnelRecords(records, currentRecords),
          );
        }
      })
      .catch(() => {
        // Keep records registered by foreground preview actions. A stale list
        // failure should not erase a tunnel that was just created locally.
      });
    return () => {
      cancelled = true;
    };
  }, [environmentApi, setTrackedDevTunnelRecords, threadId]);

  useEffect(() => {
    const browserApi = environmentApi?.browser;
    if (!browserApi) {
      return;
    }
    return browserApi.onEvent(
      buildBrowserPreviewSubscriptionInput({ environmentId, threadId }),
      (event) => {
        useBrowserStateStore.getState().applyBrowserEvent(threadRef, event);
      },
    );
  }, [environmentApi, environmentId, threadId, threadRef]);

  useEffect(() => {
    if (!environmentApi) {
      setHasBrowserRuntime(true);
      setBrowserRuntimeError(null);
      setConnecting(true);
      setPassiveAttachSettled(false);
      return () => {
        setConnecting(false);
      };
    }
    const browserApi = environmentApi.browser;
    if (!browserApi) {
      setHasBrowserRuntime(false);
      setBrowserRuntimeError("Browser runtime is not available for this environment.");
      setConnecting(false);
      setPassiveAttachSettled(true);
      return;
    }
    setHasBrowserRuntime(true);
    setBrowserRuntimeError(null);
    setConnecting(true);
    setPassiveAttachSettled(false);
    const timeout = window.setTimeout(() => {
      setConnecting(false);
      setPassiveAttachSettled(true);
    }, BROWSER_ATTACH_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timeout);
      setConnecting(false);
    };
  }, [
    environmentApi,
    environmentId,
    setBrowserRuntimeError,
    setConnecting,
    setHasBrowserRuntime,
    setPassiveAttachSettled,
    threadId,
  ]);

  useEffect(() => {
    const browserApi = environmentApi?.browser;
    if (!browserApi) {
      return;
    }
    const attachKey = `${environmentId}:${threadId}`;
    if (passiveSessionAttachKeyRef.current === attachKey) {
      return;
    }
    passiveSessionAttachKeyRef.current = attachKey;
    let cancelled = false;
    void browserApi
      .getSession({ threadId, environmentId })
      .then((snapshot) => {
        if (cancelled) {
          return;
        }
        if (snapshot) {
          setSessionSnapshot(threadRef, snapshot);
          setConnecting(false);
          setPassiveAttachSettled(true);
        }
      })
      .catch(() => {
        // Passive attach is best-effort. Explicit open/navigation actions own
        // user-visible browser errors so stale runtime state cannot block them.
      });
    return () => {
      cancelled = true;
    };
  }, [
    environmentApi,
    environmentId,
    passiveSessionAttachKeyRef,
    setConnecting,
    setPassiveAttachSettled,
    setSessionSnapshot,
    threadId,
    threadRef,
  ]);

  useEffect(() => {
    if (!session || openingBrowserSession || lifecycleAction !== null || pendingBrowserCommand) {
      return;
    }
    setConnecting(false);
    setPassiveAttachSettled(true);
  }, [
    lifecycleAction,
    openingBrowserSession,
    pendingBrowserCommand,
    session,
    setConnecting,
    setPassiveAttachSettled,
  ]);

  useEffect(() => {
    const scopeKey = `${environmentId}:${threadId}`;
    if (browserRuntimeScopeKeyRef.current === scopeKey) {
      return;
    }
    browserRuntimeScopeKeyRef.current = scopeKey;
    setBrowserRuntimeError(null);
    setConnecting(false);
    addressInputFocusedRef.current = false;
    setAddressInputFocused(false);
    setPassiveAttachSettled(false);
    setOpeningBrowserSession(false);
    commandGenerationRef.current += 1;
    pendingBrowserCommandTokenRef.current = null;
    interactivePointerQueueRef.current = Promise.resolve();
    interactiveKeyboardQueueRef.current = Promise.resolve();
    lastInteractivePointerPointRef.current = null;
    historyRestoreAttemptKeyRef.current = null;
    setPendingBrowserCommand(null);
    lastSyncedPreviewTabIdRef.current = null;
    sessionOpenInitialUrlRef.current =
      previewRememberLastUrl && previewLastUrl ? previewLastUrl : DEFAULT_BROWSER_HOME_URL;
  }, [
    addressInputFocusedRef,
    browserRuntimeScopeKeyRef,
    commandGenerationRef,
    environmentId,
    historyRestoreAttemptKeyRef,
    interactiveKeyboardQueueRef,
    interactivePointerQueueRef,
    lastInteractivePointerPointRef,
    lastSyncedPreviewTabIdRef,
    pendingBrowserCommandTokenRef,
    previewLastUrl,
    previewRememberLastUrl,
    sessionOpenInitialUrlRef,
    setAddressInputFocused,
    setBrowserRuntimeError,
    setConnecting,
    setOpeningBrowserSession,
    setPassiveAttachSettled,
    setPendingBrowserCommand,
    threadId,
  ]);
}
